import Vue from 'vue'
import axios from '@/shared/plugins/axios'
import utils from '@/shared/plugins/utils'

const isDev = process.env.NODE_ENV.toLowerCase() === 'development'

const defaultRoles = () => ({
    '*': [],
    AVM: [],
    DVM: [],
    ERS: [],
    OVM: [],
})

const auth = {
    namespaced: true,
    state: {
        authenticated: null,
        email: null,
        username: null,
        organization: null,
        roles: defaultRoles(),
        jwtInterval: null,
        showTimeoutRibbon: false,
    },
    getters: {
        getUsername(state) {
            return state.username
        },
        hasRole(state) {
            return (role, module) => {
                if (!module) {
                    for (const roles of Object.values(state.roles)) {
                        if (roles.includes(role)) {
                            return true
                        }
                    }
                    return false
                }

                return (
                    state.roles['*'].includes(role) || state.roles[module]?.includes(role)
                )
            }
        },
        accessibleModules(state) {
            return Object.entries(state.roles)
                .filter(([k, v]) => v.length && !['-', '*'].includes(k))
                .map(([k, v]) => k)
        },
    },
    mutations: {
        SET_USER(state, data) {
            state.email = data.email || null
            state.username = data.username || null
            state.authenticated = data.authenticated || null
            state.organization = data.organization || null
            state.roles = data.roles || defaultRoles()
        },
        SET_INTERVAL(state, data) {
            const cookie_polling_interval = parseFloat(
                Vue.prototype.$config.COOKIE_POLLING_INTERVAL
            )
            if (data.cb) {
                if (state.jwtInterval) {
                    clearInterval(state.jwtInterval)
                }
                state.jwtInterval = setInterval(data.cb, cookie_polling_interval * 1000)
            } else {
                clearInterval(state.jwtInterval)
                state.jwtInterval = null
            }
        },
        SET_TIMEOUTRIBBON(state, data) {
            state.showTimeoutRibbon = data.showTimeoutRibbon
        },
        TOGGLE_ROLE(state, role) {
            if (isDev) {
                utils.arrayToggle(state.roles['*'], role)
            }
        },
    },
    actions: {
        check_authentication(context) {
            return axios
                .get(utils.urlJoin(Vue.prototype.$config.AUTH_API_URL, 'user'))
                .then((response) => {
                    context.commit('SET_USER', response.data)
                })
        },
        start_interval(context) {
            context.commit('SET_INTERVAL', {
                cb: () => {
                    const cookie_polling_interval = parseFloat(
                        Vue.prototype.$config.COOKIE_POLLING_INTERVAL
                    )
                    const jwt_ = utils.getCookie('re-jwt')
                    if (jwt_ && jwt_ !== '') {
                        const parsed_ = utils.parseJwt(jwt_)
                        const { exp } = parsed_

                        if (exp * 1000 - Date.now() > 60000) return

                        if (exp * 1000 - Date.now() <= cookie_polling_interval * 1000) {
                            utils.deleteCookie('re-jwt')
                            context.commit('SET_USER', {})
                            context.commit('SET_TIMEOUTRIBBON', {
                                showTimeoutRibbon: false,
                            })
                            context.commit('SET_INTERVAL', { cb: null })
                            if (Vue.prototype.$route.name !== 'login') {
                                Vue.prototype.$router.push(
                                    '/default/login?msg=session-expired'
                                )
                            }
                            return
                        }

                        if (exp * 1000 - Date.now() <= 60000) {
                            context.commit('SET_TIMEOUTRIBBON', {
                                showTimeoutRibbon: true,
                            })
                        }
                    } else {
                        context.commit('SET_USER', {})
                        context.commit('SET_TIMEOUTRIBBON', { showTimeoutRibbon: false })
                        context.commit('SET_INTERVAL', { cb: null })
                        if (Vue.prototype.$route.name !== 'login') {
                            Vue.prototype.$router.push(
                                '/default/login?msg=session-expired'
                            )
                        }
                    }
                },
            })
        },
        end_interval(context) {
            context.commit('SET_INTERVAL', { cb: null })
        },
        refresh_token(context) {
            axios
                .get(
                    utils.urlJoin(Vue.prototype.$config.VALUATION_API_URL, [
                        'user',
                        'roles',
                    ])
                )
                .then(() => {
                    context.commit('SET_TIMEOUTRIBBON', { showTimeoutRibbon: false })
                })
        },
        update_current_user(context, user_data) {
            return axios
                .patch(`/user/${context.state.username}`, user_data)
                .then((response) => {
                    context.commit('SET_USER', { ...response.data, authenticated: true })
                })
        },
    },
}
export default auth
